import { useEffect, useState } from 'react';
import { Button, Row, Stack } from 'react-bootstrap';
import { Trans, useTranslation } from 'react-i18next';
import OtpInput from 'react-otp-input';

import {
  APP_EVENTS,
  captureAppEvent,
  LOGIN_METHOD,
} from 'src/services/posthogEvents';

import { Api } from 'src/models/Api.model';
import { AxiosResponseWithRefreshToken } from 'src/models/GalenData/Request.model';
import { GalenDataUser } from 'src/models/GalenData/User.model';
import { I18NextNS } from 'src/models/Language.model';

import useHandleHttpRequestError from 'src/hooks/useHandleHttpRequestError';

interface MFALoginProps {
  xRequestToken: string;
  validateUserRole: (
    res: AxiosResponseWithRefreshToken<GalenDataUser>,
  ) => boolean;
  onVerificationSuccess: (
    res: AxiosResponseWithRefreshToken<GalenDataUser>,
  ) => void | Promise<void>;
  setIsLoggingIn: (value: boolean) => void;
  isLoggingIn: boolean;
}

const MFALogin = ({
  xRequestToken,
  validateUserRole,
  onVerificationSuccess,
  setIsLoggingIn,
  isLoggingIn,
}: MFALoginProps) => {
  const { t } = useTranslation(I18NextNS.PAGES);
  const [otp, setOtp] = useState('');
  const [countdownSeconds, setCountdownSeconds] = useState(30);
  const { showHttpRequestError } = useHandleHttpRequestError();

  useEffect(() => {
    if (countdownSeconds > 0) {
      const intervalId = setInterval(() => {
        setTimeout(() => {
          setCountdownSeconds((prevSeconds) => prevSeconds - 1);
        }, 500);
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [countdownSeconds]);

  const handleVerifyOTP = async (otp: string) => {
    try {
      const res = await Api.remote.verifyMFACode(otp, xRequestToken);

      if (!validateUserRole(res)) {
        setIsLoggingIn(false);
        return;
      }

      await onVerificationSuccess(res);

      captureAppEvent(APP_EVENTS.LOGIN_SUCCEEDED, {
        method: LOGIN_METHOD.PASSWORD,
        mfa_used: true,
      });
    } catch (err) {
      captureAppEvent(APP_EVENTS.LOGIN_FAILED, { method: LOGIN_METHOD.MFA });
      showHttpRequestError(err);
    }
  };

  const handleResendCode = async () => {
    try {
      await Api.remote.sendMFACode(xRequestToken);
      setCountdownSeconds(30);
    } catch (err) {
      showHttpRequestError(err);
    }
  };

  return (
    <Stack className="justify-content-center p-4" gap={4}>
      <Stack className="text-center" gap={2}>
        <h2 data-test="verify-account-title">
          {t('loginLogout.verifyAccount')}
        </h2>
        <p data-test="verify-account-description">
          {t('loginLogout.verifyAccountCode')}
        </p>
      </Stack>
      <Stack gap={4}>
        <OtpInput
          inputType="tel"
          containerStyle={{ display: 'flex', justifyContent: 'center' }}
          value={otp}
          onChange={setOtp}
          numInputs={6}
          renderSeparator={<span className="mx-2">-</span>}
          inputStyle={{
            width: '3rem',
            height: '3rem',
            borderRadius: '0.25rem',
            border: '1px solid #ced4da',
            textAlign: 'center',
            fontSize: '1.5rem',
          }}
          renderInput={(props, index) => (
            <input {...props} data-test={`otp-input-${index}`} />
          )}
          shouldAutoFocus
        />
        <div>
          {isLoggingIn && (
            <p className="alert alert-info">
              {t('loginLogout.loggingInPleaseWait')}
            </p>
          )}
        </div>
        <Row>
          <Button
            onClick={() => handleVerifyOTP(otp)}
            disabled={otp.length < 6}
            data-test="verify-account-confirm-button"
          >
            {t('loginLogout.confirm')}
          </Button>
        </Row>
        <Stack className="justify-content-center">
          {countdownSeconds > 0 ? (
            <p className="text-center" data-test="resend-code-countdown-timer">
              <Trans
                i18nKey="loginLogout.resendCodeInSeconds"
                ns="pages"
                values={{ countdownSeconds }}
                components={[
                  <strong data-test="countdown-seconds">
                    {countdownSeconds}
                  </strong>,
                ]}
              />
            </p>
          ) : (
            <Stack direction="horizontal" className="justify-content-center">
              <span data-test="did-not-receive-code-text">
                {t('loginLogout.didNotReceiveCode')}
              </span>
              <Button
                variant="link"
                className="p-0"
                onClick={handleResendCode}
                data-test="resend-code-link"
                data-track="auth.mfa.resend-code"
              >
                {t('loginLogout.resendCode')}
              </Button>
            </Stack>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default MFALogin;
